import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Check, Loader2, PartyPopper } from 'lucide-react'
import RoomNotFound from '@/components/PageNotFound/RoomNotFound'
import { cn } from '@/lib/utils'
import { useWorldGraph } from '../hooks/useWorldGraph'
import type { WorldGraphState } from '../types'
import WorldGraphSwitchCard from '../components/WorldGraphSwitchCard'
import ScenarioPhase, { MIN_SCENARIO_CHARS } from '../components/ScenarioPhase'
import SidecarPhase from '../components/SidecarPhase'
import StocksPhase from '../components/StocksPhase'
import LinksPhase from '../components/LinksPhase'
import TeamsPhase from '../components/TeamsPhase'

const STEPS = [
  { label: 'Scenario', section: 'world-step-scenario' },
  { label: 'Baseline context', section: 'world-step-sidecar' },
  { label: 'Stocks', section: 'world-step-stocks' },
  { label: 'Causal links', section: 'world-step-links' },
]

/** The step chips, each a jump to its section once that section is on the page. */
function StepTracker({ done, ready }: { done: number; ready: boolean }) {
  const jump = (section: string) =>
    document
      .getElementById(section)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  return (
    <ol className='flex flex-wrap gap-2' aria-label='World graph build steps'>
      {STEPS.map(({ label, section }, index) => {
        const complete = index < done
        const current = index === done
        return (
          <li key={label}>
            <button
              type='button'
              disabled={index > done}
              onClick={() => jump(section)}
              className={cn(
                'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm',
                complete &&
                  'border-green-600/30 bg-green-50 text-green-700 hover:bg-green-100',
                current &&
                  'border-dark-blue bg-dark-blue text-white hover:bg-dark-blue-hovered',
                !complete &&
                  !current &&
                  'cursor-default border-gray-200 bg-white text-gray-500'
              )}
            >
              {complete ? (
                <Check className='h-3.5 w-3.5' aria-hidden />
              ) : (
                <span className='text-xs'>{index + 1}</span>
              )}
              {label}
            </button>
          </li>
        )
      })}
      {ready && (
        <li className='inline-flex items-center gap-2 rounded-full border border-green-600 bg-green-600 px-3 py-1 text-sm text-white'>
          <PartyPopper className='h-3.5 w-3.5' aria-hidden />
          World graph ready
        </li>
      )}
    </ol>
  )
}

/** Team ids to display names, from the baseline roster and the stock proposal. */
function teamNamesOf(state: WorldGraphState): Record<string, string> {
  return Object.fromEntries(state.teams.map((t) => [t.id, t.name]))
}

export default function WorldGraphSetupPage() {
  const { roomId } = useParams<{ roomId: string }>()
  const wg = useWorldGraph(roomId)
  const { state } = wg
  const [scenarioText, setScenarioText] = useState('')
  const savedScenario = state?.scenario_document ?? ''
  const teamNames = useMemo(() => (state ? teamNamesOf(state) : {}), [state])

  useEffect(() => {
    setScenarioText(savedScenario)
  }, [savedScenario])

  if (wg.loading && !state) {
    return (
      <div className='flex justify-center py-16 text-dark-blue'>
        <Loader2 className='h-6 w-6 animate-spin' aria-label='Loading' />
      </div>
    )
  }
  if (wg.loadError?.status === 404) return <RoomNotFound />
  if (!state) {
    return (
      <div className='container mx-auto max-w-5xl px-4 py-8'>
        <div className='rounded-md bg-red-50 p-6 text-red-700'>
          {wg.loadError?.message || 'Could not load the world graph.'}
        </div>
      </div>
    )
  }

  const scenarioReady = scenarioText.trim().length >= MIN_SCENARIO_CHARS
  const done = [
    savedScenario.trim().length >= MIN_SCENARIO_CHARS,
    state.sidecar_approved,
    state.stocks_approved,
    state.links_approved,
  ].findIndex((stepDone) => !stepDone)
  const { nodes, edges } = state.graph

  // Writing starts from the saved scenario, so unsaved edits are saved first.
  const generateSidecar = async () => {
    if (
      scenarioText !== savedScenario &&
      !(await wg.save({ scenario_document: scenarioText }))
    )
      return false
    return wg.generate('sidecar')
  }

  return (
    <div className='min-h-screen'>
      <div className='container mx-auto max-w-6xl space-y-6 px-4 py-8'>
        <WorldGraphSwitchCard
          enabled={state.enabled}
          onChange={wg.setEnabled}
        />

        {state.enabled && (
          <>
            <StepTracker
              done={done === -1 ? STEPS.length : done}
              ready={state.ready}
            />
            <div id='world-step-scenario' className='scroll-mt-4'>
              <ScenarioPhase
                text={scenarioText}
                saved={savedScenario}
                onChange={setScenarioText}
                locked={wg.running || state.sidecar_approved}
                lockedReason={
                  wg.running
                    ? 'Locked while a build step runs.'
                    : 'Locked: the baseline context built from it is approved.'
                }
                onSave={(scenario_document) => wg.save({ scenario_document })}
              />
            </div>
            <div id='world-step-sidecar' className='scroll-mt-4'>
              <SidecarPhase
                state={state}
                anyRunning={wg.running}
                canStart={scenarioReady}
                cannotStartReason={`Add the scenario above first (at least ${MIN_SCENARIO_CHARS} characters).`}
                onGenerate={generateSidecar}
                onSave={(sidecar) => wg.save({ sidecar })}
                onApprove={(sidecar) => wg.approve('sidecar', { sidecar })}
                onReopen={() => wg.reopen('sidecar')}
              />
            </div>
            {state.sidecar_approved && roomId && (
              <TeamsPhase
                roomId={roomId}
                setup={state.team_setup}
                onSetUp={wg.setUpTeams}
              />
            )}
            {(state.sidecar_approved || nodes.length > 0) && (
              <div id='world-step-stocks' className='scroll-mt-4'>
                <StocksPhase
                  state={state}
                  anyRunning={wg.running}
                  teamNames={teamNames}
                  onGenerate={() => wg.generate('stocks')}
                  onSave={(edited) => wg.save({ nodes: edited })}
                  onApprove={(edited) =>
                    wg.approve('stocks', { nodes: edited })
                  }
                  onReopen={() => wg.reopen('stocks')}
                />
              </div>
            )}
            {(state.stocks_approved || edges.length > 0) && (
              <div id='world-step-links' className='scroll-mt-4'>
                <LinksPhase
                  state={state}
                  anyRunning={wg.running}
                  teamNames={teamNames}
                  onGenerate={() => wg.generate('links')}
                  onFinalize={() => wg.generate('finalize')}
                  onSave={(edited) => wg.save({ edges: edited })}
                  onApprove={(edited) => wg.approve('links', { edges: edited })}
                  onReopen={() => wg.reopen('links')}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
